import cuid from "cuid";
import { deburr, get, sortBy } from "lodash";
import { NODE_TYPES } from "utils/constants";
import { newRuleEdge, newStatusNode } from "./graph";
import { newStepPosition, withHandles } from "./layout";
import { STEP_KINDS, stepKind } from "./step-tabs";

export const IGNORED_REASONS = {
  NOT_IN_FOLDER: "NOT_IN_FOLDER",
  TOO_DEEP: "TOO_DEEP",
  FOLDER_NOT_INDEXED: "FOLDER_NOT_INDEXED",
  NOT_PNG: "NOT_PNG",
  FILE_NOT_INDEXED: "FILE_NOT_INDEXED",
  DUPLICATE_INDEX: "DUPLICATE_INDEX",
};

export const IMPORT_ACTIONS = {
  CREATE: "CREATE",
  UPDATE: "UPDATE",
  CONFLICT: "CONFLICT",
};

const INDEXED = /^(\d+)(?:[\s._-]+(.*))?$/;
const PNG = /\.png$/i;
const HIDDEN = /^\./;
const STEP_DEPTH = 3;

export const normalizeName = (value) =>
  deburr(String(value || ""))
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

const indexedOf = (name) => {
  const match = INDEXED.exec(String(name).trim());
  if (!match) return null;
  return { index: Number(match[1]), label: (match[2] || "").trim() };
};

const withoutDuplicates = (captures, ignored) =>
  sortBy(captures, ["index", "path"]).reduce((kept, capture) => {
    if (kept.some(({ index }) => index === capture.index)) {
      ignored.push({
        path: capture.path,
        reason: IGNORED_REASONS.DUPLICATE_INDEX,
      });
      return kept;
    }
    return [...kept, capture];
  }, []);

export const readCaptureFolder = (files = []) => {
  const ignored = [];
  const folders = new Map();
  let root = "";
  for (const file of files) {
    const path = file.webkitRelativePath || file.name;
    const parts = path.split("/");
    const fileName = parts[parts.length - 1];
    root = root || parts[0];
    if (HIDDEN.test(fileName)) continue;
    if (parts.length < STEP_DEPTH) {
      ignored.push({ path, reason: IGNORED_REASONS.NOT_IN_FOLDER });
      continue;
    }
    if (parts.length > STEP_DEPTH) {
      ignored.push({ path, reason: IGNORED_REASONS.TOO_DEEP });
      continue;
    }
    const folderName = parts[1];
    const folder = indexedOf(folderName);
    if (!folder || !folder.label) {
      ignored.push({ path, reason: IGNORED_REASONS.FOLDER_NOT_INDEXED });
      continue;
    }
    if (!PNG.test(fileName)) {
      ignored.push({ path, reason: IGNORED_REASONS.NOT_PNG });
      continue;
    }
    const capture = indexedOf(fileName.replace(PNG, ""));
    if (!capture) {
      ignored.push({ path, reason: IGNORED_REASONS.FILE_NOT_INDEXED });
      continue;
    }
    const entry = folders.get(folderName) || {
      folder: folderName,
      index: folder.index,
      name: folder.label,
      captures: [],
    };
    entry.captures.push({ ...capture, file, path });
    folders.set(folderName, entry);
  }
  const steps = sortBy([...folders.values()], ["index", "name"]).map(
    ({ captures, ...step }) => {
      const [screen, ...states] = withoutDuplicates(captures, ignored);
      return { ...step, screen, states };
    },
  );
  return { root, steps, ignored };
};

export const planImport = ({ steps, nodes }) => {
  const used = new Set();
  return steps.map((step) => {
    const node = nodes.find(
      (candidate) =>
        !used.has(candidate.id) &&
        normalizeName(get(candidate, "data.name")) === normalizeName(step.name),
    );
    if (!node) return { ...step, action: IMPORT_ACTIONS.CREATE };
    used.add(node.id);
    if (stepKind(get(node, "data.nodeType")) !== STEP_KINDS.SCREEN)
      return { ...step, action: IMPORT_ACTIONS.CONFLICT, node };
    return { ...step, action: IMPORT_ACTIONS.UPDATE, node };
  });
};

const hasLink = (edges, source, target) =>
  edges.some((edge) => edge.source === source && edge.target === target);

export const applyPlanToGraph = ({ plan, nodes, edges, direction }) => {
  let nextNodes = [...nodes];
  let nextEdges = [...edges];
  let order = nodes.reduce(
    (max, node) => Math.max(max, parseInt(get(node, "data.order"), 10) || 0),
    0,
  );
  let previous = null;
  const steps = [];
  for (const step of plan) {
    if (step.action === IMPORT_ACTIONS.CONFLICT) continue;
    let node = step.node;
    if (step.action === IMPORT_ACTIONS.CREATE) {
      order += 1;
      [node] = withHandles(
        [
          newStatusNode({
            id: cuid(),
            name: step.name,
            nodeType: NODE_TYPES.STATUS,
            order: String(order),
            position: newStepPosition(nextNodes, previous, direction),
          }),
        ],
        direction,
      );
      nextNodes = [...nextNodes, node];
    }
    if (previous && !hasLink(nextEdges, previous.id, node.id))
      nextEdges = [
        ...nextEdges,
        newRuleEdge({
          connection: { source: previous.id, target: node.id },
          id: cuid(),
          edges: nextEdges,
        }),
      ];
    previous = node;
    steps.push({ ...step, node });
  }
  return { nodes: nextNodes, edges: nextEdges, steps };
};
